import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon, PencilIcon, XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { studentAPI } from '../../services/api';
import { formatDateToIST } from '../../utils/dateUtils';
import { useDeactivateStudent, useActivateStudent } from '../../hooks/useStudents';
import ConfirmationModal from '../ConfirmationModal';

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const deactivateMutation = useDeactivateStudent();
  const activateMutation = useActivateStudent();

  const { data: student, isLoading, error } = useQuery({
    queryKey: ['student', id],
    queryFn: () => {
      if (!id || id === 'undefined') {
        throw new Error('Invalid student ID');
      }
      return studentAPI.getById(id).then(res => res.data);
    },
    enabled: !!id && id !== 'undefined',
  });

  const handleStatusToggle = () => {
    if (student) {
      if (student.is_active) {
        deactivateMutation.mutate(id);
      } else {
        activateMutation.mutate(id);
      }
      setShowDeactivateModal(false);
    }
  };

  if (!id || id === 'undefined') {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Invalid student ID. Please select a valid student.</p>
        <button
          onClick={() => navigate('/students')}
          className="mt-2 text-blue-400 hover:text-blue-300"
        >
          Back to Students
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Error loading student: {error?.message || 'Student not found'}</p>
        <button
          onClick={() => navigate('/students')}
          className="mt-2 text-blue-400 hover:text-blue-300"
        >
          Back to Students
        </button>
      </div>
    );
  }



  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/students')}
          className="flex items-center text-gray-300 hover:text-gray-100 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Students
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">
              {student.first_name} {student.last_name}
            </h1>
            <p className="text-gray-300 mt-2">Student ID: {student.student_id}</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowDeactivateModal(true)}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                student.is_active
                  ? 'bg-orange-600 hover:bg-orange-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {student.is_active ? (
                <>
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  Deactivate Student
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Activate Student
                </>
              )}
            </button>
            <Link
              to={`/students/${id}/edit`}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PencilIcon className="h-5 w-5 mr-2" />
              Edit Student
            </Link>
          </div>
        </div>
      </div>

      {/* Student Information */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Personal Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300">Full Name</label>
            <p className="mt-1 text-sm text-white">{student.first_name} {student.last_name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Email</label>
            <p className="mt-1 text-sm text-white">{student.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Phone</label>
            <p className="mt-1 text-sm text-white">{student.phone}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Date of Birth</label>
            <p className="mt-1 text-sm text-white">
              {formatDateToIST(student.date_of_birth)}
            </p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300">Address</label>
            <p className="mt-1 text-sm text-white">{student.address || 'Not provided'}</p>
          </div>
        </div>
      </div>

      {/* Status Information */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Status Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300">Status</label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full mt-1 ${
              student.is_active
                ? 'bg-green-900/30 text-green-300 border border-green-700'
                : 'bg-red-900/30 text-red-300 border border-red-700'
            }`}>
              {student.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300">Admission Date</label>
            <p className="mt-1 text-sm text-white">
              {formatDateToIST(student.admission_date)}
            </p>
          </div>
        </div>
      </div>

      {/* Deactivate/Activate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={handleStatusToggle}
        title={student.is_active ? "Confirm Deactivation" : "Confirm Activation"}
        message={
          student.is_active
            ? `Are you sure you want to deactivate ${student.first_name} ${student.last_name}? They will not appear in attendance or analytics while deactivated. You can reactivate them later.`
            : `Are you sure you want to activate ${student.first_name} ${student.last_name}? They will appear in attendance and analytics again.`
        }
        confirmText={student.is_active ? "Deactivate" : "Activate"}
        cancelText="Cancel"
        type={student.is_active ? "danger" : "success"}
        confirmButtonVariant={student.is_active ? "danger" : "success"}
      />
    </div>
  );
};

export default StudentDetail; 